import React, { useState, useEffect } from 'react';
import { Star, Upload, X, CheckCircle, Loader2, AlertCircle, Image as ImageIcon, Video as VideoIcon } from 'lucide-react';
import { api } from '../services/api';
import { r2Upload } from '../services/r2-upload';
import { ReviewToken, ReviewSubmissionData } from '../types';

interface MediaFile {
  file: File;
  type: 'image' | 'video';
  preview: string;
}

export const ReviewSubmission: React.FC = () => {
  // UI State
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [media, setMedia] = useState<MediaFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Token State
  const [tokenData, setTokenData] = useState<ReviewToken | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);

  // Get token from URL path: /review/:token
  const urlPath = window.location.pathname;
  const tokenFromUrl = urlPath.split('/').pop();
  const isTokenSubmission = tokenFromUrl && tokenFromUrl !== 'review' && tokenFromUrl !== 'submit';

  // Load token data if token-based submission
  useEffect(() => {
    if (isTokenSubmission) {
      validateToken(tokenFromUrl!);
    } else {
      setTokenLoading(false);
    }
  }, [tokenFromUrl]);

  const validateToken = async (token: string) => {
    try {
      const data = await api.validateReviewToken(token);

      if (!data) {
        throw new Error('Invalid or expired review link. Please contact support if you need a new link.');
      }

      setTokenData(data);
      
      // Pre-fill customer info
      if (data.customer_name) setName(data.customer_name);
      if (data.customer_email) setEmail(data.customer_email);

    } catch (e: any) {
      setTokenError(e.message);
    } finally {
      setTokenLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    
    // Limit to 5 files total
    if (media.length + files.length > 5) {
      alert('Maximum 5 photos/videos allowed');
      return;
    }

    // Validate file sizes (max 10MB per file)
    const oversizedFiles = files.filter(f => f.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      alert('Each file must be under 10MB');
      return;
    }

    // Validate file types
    const allowedTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/quicktime'
    ];

    const invalidFiles = files.filter(f => !allowedTypes.includes(f.type));
    if (invalidFiles.length > 0) {
      alert('Only images (JPG, PNG, GIF, WEBP) and videos (MP4, WEBM, MOV) are allowed');
      return;
    }

    const newMedia: MediaFile[] = files.map(file => ({
      file,
      type: file.type.startsWith('video/') ? 'video' : 'image',
      preview: URL.createObjectURL(file)
    }));

    setMedia([...media, ...newMedia]);
  };

  const removeMedia = (index: number) => {
    const updated = [...media];
    URL.revokeObjectURL(updated[index].preview);
    updated.splice(index, 1);
    setMedia(updated);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (rating === 0) {
      alert('Please select a rating');
      return;
    }

    if (!content.trim() || content.trim().length < 10) {
      alert('Please write at least 10 characters in your review');
      return;
    }

    if (!name.trim()) {
      alert('Please enter your name');
      return;
    }

    // Determine submission data
    let submissionData: ReviewSubmissionData;

    if (isTokenSubmission && tokenData) {
      submissionData = {
        token: tokenFromUrl,
        product_id: tokenData.product_id,
        order_id: tokenData.order_id,
        rating,
        content: content.trim(),
        reviewer_name: name.trim(),
        reviewer_email: email.trim() || undefined,
        reviewer_phone: tokenData.customer_phone || undefined
      };
    } else {
      // Direct submission (fallback - should not happen in production)
      alert('Invalid submission method. Please use the link from your email or WhatsApp message.');
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);

    try {
      // 1. Upload media files if any
      let mediaUrls: Array<{ type: 'image' | 'video'; url: string }> = [];
      
      if (media.length > 0) {
        console.log('📤 Uploading media files...');
        
        for (let i = 0; i < media.length; i++) {
          const file = media[i];
          setUploadProgress(Math.round(((i + 1) / media.length) * 50)); // 0-50% for uploads
          
          try {
            const result = await r2Upload.uploadReviewMedia(
              file.file,
              tokenData!.admin_id,
              undefined // review_id not known yet
            );
            
            mediaUrls.push({
              type: file.type,
              url: result.url
            });
          } catch (uploadError: any) {
            console.error('Upload error:', uploadError);
            // Continue with other files even if one fails
          }
        }
      }

      setUploadProgress(60);

      // 2. Submit review with media URLs
      const reviewData: ReviewSubmissionData = {
        ...submissionData,
        media_files: undefined // Remove files, add URLs separately
      };

      // Add media_urls to the submission (edge function expects this)
      const response = await api.submitPublicReview({
        ...reviewData,
        media_urls: mediaUrls as any
      });

      setUploadProgress(100);

      if (response.success) {
        setIsSuccess(true);

        // Clean up media previews
        media.forEach(m => URL.revokeObjectURL(m.preview));
      } else {
        throw new Error('Review submission failed');
      }

    } catch (e: any) {
      console.error('Submission error:', e);
      alert(e.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (tokenLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Validating review link...</p>
        </div>
      </div>
    );
  }

  // Token error state
  if (tokenError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-sm border border-slate-200 p-8 text-center">
          <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Invalid Link</h2>
          <p className="text-slate-600 mb-6">{tokenError}</p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-slate-700">
            <p className="font-medium mb-1">Need help?</p>
            <p>Contact support or check your email for a valid review link.</p>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-slate-50">
        <div className="max-w-md w-full text-center space-y-4 animate-in zoom-in-95 duration-300">
          <div className="w-20 h-20 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={48} />
          </div>
          <h2 className="text-3xl font-bold text-slate-800">Thank You!</h2>
          <p className="text-slate-600 text-lg">
            Your review has been submitted successfully.
          </p>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-700">
            <p className="font-medium mb-1">What happens next?</p>
            <p>Your review will be published after moderation. This usually takes 1-2 business days.</p>
          </div>
        </div>
      </div>
    );
  }

  // Review form
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto bg-white rounded-3xl shadow-xl border border-slate-200 overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-8 text-white text-center">
          <h1 className="text-3xl font-bold mb-2">Share Your Experience</h1>
          <p className="text-blue-100">Your feedback helps us improve and helps other customers make better decisions</p>
          {tokenData && tokenData.order_id && (
            <div className="mt-4 inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm">
              Order: <span className="font-mono font-bold">{tokenData.order_id}</span>
            </div>
          )}
        </div>

        <div className="p-8 space-y-8">
          
          {/* Star Rating */}
          <div className="flex flex-col items-center gap-4">
            <label className="text-lg font-semibold text-slate-700">How would you rate your experience?</label>
            <div className="flex gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHover(star)}
                  onMouseLeave={() => setHover(0)}
                  className="p-2 transition-transform hover:scale-110 active:scale-95"
                >
                  <Star 
                    size={48} 
                    className={`${
                      (hover || rating) >= star 
                        ? "fill-amber-400 text-amber-400" 
                        : "text-slate-300"
                    } transition-colors`}
                  />
                </button>
              ))}
            </div>
            {rating > 0 && (
              <p className="text-sm font-bold text-amber-600 uppercase tracking-widest">
                {rating === 5 ? '⭐ Excellent!' : rating === 4 ? '😊 Great!' : rating === 3 ? '👍 Good' : rating === 2 ? '😕 Fair' : '😞 Poor'}
              </p>
            )}
          </div>

          {/* Review Text */}
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700 uppercase tracking-wider">
              Write Your Review <span className="text-red-500">*</span>
            </label>
            <textarea 
              required
              placeholder="What did you like or dislike? How was the quality, delivery, and overall experience?"
              className="w-full min-h-[140px] p-4 bg-slate-50 border-2 border-slate-200 rounded-2xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm resize-none"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              disabled={isSubmitting}
              maxLength={1000}
            />
            <p className="text-xs text-slate-500 text-right">{content.length}/1000 characters</p>
          </div>

          {/* Media Uploads */}
          <div className="space-y-3">
            <label className="text-sm font-bold text-slate-700 uppercase tracking-wider">
              Add Photos or Videos (Optional)
            </label>
            <div className="grid grid-cols-3 gap-3">
              {media.map((m, i) => (
                <div key={i} className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 border-2 border-slate-200 group">
                  {m.type === 'video' ? (
                    <video src={m.preview} className="w-full h-full object-cover" />
                  ) : (
                    <img src={m.preview} alt="preview" className="w-full h-full object-cover" />
                  )}
                  <button 
                    type="button"
                    onClick={() => removeMedia(i)}
                    className="absolute top-2 right-2 p-1.5 bg-black/70 text-white rounded-full hover:bg-black transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <X size={14} />
                  </button>
                  <div className="absolute bottom-2 left-2 px-2 py-0.5 bg-black/70 text-white text-[10px] font-bold rounded uppercase flex items-center gap-1">
                    {m.type === 'video' ? <VideoIcon size={10} /> : <ImageIcon size={10} />}
                    {m.type}
                  </div>
                </div>
              ))}
              
              {media.length < 5 && (
                <label className="aspect-square flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-xl cursor-pointer hover:bg-slate-50 hover:border-blue-400 transition-colors text-slate-400 hover:text-blue-500">
                  <Upload size={28} />
                  <span className="text-[10px] font-bold uppercase mt-2 text-center px-2">
                    {media.length === 0 ? 'Add Media' : `Add More (${5 - media.length} left)`}
                  </span>
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*,video/*" 
                    className="hidden" 
                    onChange={handleFileChange}
                    disabled={isSubmitting}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-slate-500">Maximum 5 files, 10MB each. Supported: JPG, PNG, GIF, WEBP, MP4, WEBM, MOV</p>
          </div>

          {/* Customer Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                Your Name <span className="text-red-500">*</span>
              </label>
              <input 
                required
                type="text"
                placeholder="John Doe"
                className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                Email Address (Optional)
              </label>
              <input 
                type="email"
                placeholder="john@example.com"
                className="w-full p-3 bg-slate-50 border-2 border-slate-200 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Verified Badge Info */}
          {tokenData && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
              <CheckCircle className="text-green-600 mt-0.5 flex-shrink-0" size={20} />
              <div className="text-sm">
                <p className="font-semibold text-green-800">Verified Purchase</p>
                <p className="text-green-700 mt-1">This review will be marked as verified since it's linked to your order.</p>
              </div>
            </div>
          )}

          {/* Upload Progress Bar */}
          {isSubmitting && uploadProgress > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold text-blue-800">
                  {uploadProgress < 60 ? 'Uploading media...' : 'Submitting review...'}
                </span>
                <span className="text-sm text-blue-600">{uploadProgress}%</span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full bg-blue-600 transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || rating === 0}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-2xl font-bold text-lg disabled:from-slate-300 disabled:to-slate-400 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-3 shadow-lg hover:shadow-xl active:scale-[0.99]"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="animate-spin" size={24} /> 
                <span>Submitting Review...</span>
              </>
            ) : (
              <>
                <CheckCircle size={24} />
                <span>Submit Review</span>
              </>
            )}
          </button>

          <p className="text-center text-xs text-slate-500 mt-4">
            By submitting, you agree that your review may be published on our website and shared with other customers.
          </p>
        </div>
      </form>
    </div>
  );
};
